import React, { useEffect, useRef, useState } from "react";

// Perlin noise, http://mrl.nyu.edu/~perlin/noise/
class Noise {
    p: Uint8Array;
    octaves: number;

    constructor(octaves = 1) {
        this.p = new Uint8Array(512);
        this.octaves = octaves;
        // Repeat the table so the lattice wraps seamlessly at 256
        for (let i = 0; i < 256; ++i) {
            this.p[i] = Math.random() * 256;
            this.p[i + 256] = this.p[i];
        }
    }

    lerp(t: number, a: number, b: number) {
        return a + t * (b - a);
    }

    grad2d(i: number, x: number, y: number) {
        const v = (i & 1) === 0 ? x : y;
        return (i & 2) === 0 ? -v : v;
    }

    noise2d(x2d: number, y2d: number) {
        const X = Math.floor(x2d) & 255;
        const Y = Math.floor(y2d) & 255;
        const x = x2d - Math.floor(x2d);
        const y = y2d - Math.floor(y2d);
        const fx = (3 - 2 * x) * x * x;
        const fy = (3 - 2 * y) * y * y;
        const p0 = this.p[X] + Y;
        const p1 = this.p[X + 1] + Y;
        return this.lerp(
            fy,
            this.lerp(
                fx,
                this.grad2d(this.p[p0], x, y),
                this.grad2d(this.p[p1], x - 1, y),
            ),
            this.lerp(
                fx,
                this.grad2d(this.p[p0 + 1], x, y - 1),
                this.grad2d(this.p[p1 + 1], x - 1, y - 1),
            ),
        );
    }

    noise(x: number, y: number) {
        let e = 1;
        let k = 1;
        let s = 0;
        for (let i = 0; i < this.octaves; ++i) {
            e *= 0.5;
            s += (e * (1 + this.noise2d(k * x, k * y))) / 2;
            k *= 2;
        }
        return s;
    }
}

const LINES = 300;
// Noise distance between neighbouring lines, lower keeps them parallel
const LINE_SPREAD = 0.009;
// Mean of the 2 octave noise, used to center the veil and gusts
const NOISE_MEAN = 0.375;
const POINT_STEP = 2;
// MORPH lets each line take the shape of its neighbour so the veil
// regenerates. Gusts are a second noise field travelling with the wind that
// warps the veil, so ripples run along the lines instead of the whole shape
// sliding. SCROLL drifts the whole veil along with the wind, barely
// noticeable. Speeds are noise units per second.
const MORPH = 0.06;
const GUST_SPEED = 0.4;
const SCROLL = 0.012;
const GUST_WARP = 0.3;
const GUST_LIFT = 30;
const LINE_ALPHA = 0.12;
// Thin translucent lines look the same above 1.5x, and full retina on large
// screens costs a lot of GPU memory
const MAX_DPR = 1.5;
// Cap at ~60fps. The slack keeps frame spacing even, vsync timestamps jitter
// by fractions of a millisecond and an exact limit skips every other frame.
const FRAME_INTERVAL = 1000 / 60 - 4;
// After warming up, sample the gaps between draws for SAMPLE_MS and stop on
// the current frame if the median shows the animation can't run fluidly.
// Sampling by time keeps slow devices from janking longer than fast ones.
// Gaps longer than RESET_GAP mean the tab was hidden or throttled, so
// sampling restarts.
const WARMUP_FRAMES = 10;
const SAMPLE_MS = 1000;
const MIN_SAMPLES = 5;
const MAX_MEDIAN_GAP = 25;
const RESET_GAP = 250;

const getDpr = () => Math.min(window.devicePixelRatio || 1, MAX_DPR);

type Renderer = (points: Float32Array, pointsPerLine: number) => void;

const computeVeil = (
    width: number,
    height: number,
    seconds: number,
    perlin: Noise,
    gusts: Noise,
): { points: Float32Array; pointsPerLine: number } => {
    // Map the 200x200 turtle space so it covers the canvas
    const scale = Math.max(width, height) / 200;
    const morph = seconds * MORPH;
    const gustOffset = seconds * GUST_SPEED;
    const scroll = seconds * SCROLL;
    // Extend past the edges so lines always reach both sides
    const reach = Math.ceil(width / 2 / scale / POINT_STEP) * POINT_STEP;
    const pointsPerLine = (2 * reach) / POINT_STEP + 1;
    const points = new Float32Array(LINES * pointsPerLine * 2);

    let n = 0;
    for (let i = 0; i < LINES; i++) {
        for (let j = -reach; j <= reach; j += POINT_STEP) {
            const gust =
                gusts.noise(j * 0.02 - gustOffset, i * 0.001 + morph) -
                NOISE_MEAN;
            const h = perlin.noise(
                100 + j * 0.01 - scroll + gust * GUST_WARP,
                100 + i * LINE_SPREAD + morph,
            );
            points[n++] = width / 2 + j * scale;
            points[n++] =
                height / 2 +
                (0.1 * (i - LINES / 2) +
                    (h - NOISE_MEAN) * 200 +
                    gust * GUST_LIFT) *
                    scale;
        }
    }
    return { points, pointsPerLine };
};

const VERTEX_SHADER = `
attribute vec2 a_point;
uniform vec2 u_size;
void main() {
    vec2 clip = a_point / u_size * 2.0 - 1.0;
    gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
}`;

const FRAGMENT_SHADER = `
precision mediump float;
uniform float u_alpha;
void main() {
    gl_FragColor = vec4(u_alpha, u_alpha, u_alpha, u_alpha);
}`;

// Canvas 2D strokes are rasterized on the CPU in some browsers (Firefox),
// which can't keep up with 300 translucent lines. WebGL draws them on the GPU.
const createWebGlRenderer = (
    canvas: HTMLCanvasElement,
): Renderer | undefined => {
    const gl = canvas.getContext("webgl", { antialias: true });
    if (!gl) {
        return undefined;
    }
    const compile = (type: number, source: string) => {
        const shader = gl.createShader(type);
        if (!shader) {
            return undefined;
        }
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        return shader;
    };
    const program = gl.createProgram();
    const vertex = compile(gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!program || !vertex || !fragment) {
        return undefined;
    }
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        return undefined;
    }
    // biome-ignore lint/correctness/useHookAtTopLevel: WebGL call, not a React hook
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    const pointLocation = gl.getAttribLocation(program, "a_point");
    gl.enableVertexAttribArray(pointLocation);
    gl.vertexAttribPointer(pointLocation, 2, gl.FLOAT, false, 0, 0);
    const sizeLocation = gl.getUniformLocation(program, "u_size");
    // WebGL lines are always 1 device pixel, slightly wider than the 0.6 css
    // pixel strokes, so scale alpha to keep the same density
    gl.uniform1f(
        gl.getUniformLocation(program, "u_alpha"),
        LINE_ALPHA * 0.6 * getDpr(),
    );
    // Premultiplied source-over, same as canvas 2D
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    return (points, pointsPerLine) => {
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.uniform2f(sizeLocation, canvas.width, canvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.bufferData(gl.ARRAY_BUFFER, points, gl.DYNAMIC_DRAW);
        for (let i = 0; i < LINES; i++) {
            gl.drawArrays(gl.LINE_STRIP, i * pointsPerLine, pointsPerLine);
        }
    };
};

const createCanvas2dRenderer = (
    canvas: HTMLCanvasElement,
): Renderer | undefined => {
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        return undefined;
    }
    return (points, pointsPerLine) => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = `rgba(255, 255, 255, ${LINE_ALPHA})`;
        ctx.lineWidth = 0.6 * getDpr();
        let n = 0;
        for (let i = 0; i < LINES; i++) {
            ctx.beginPath();
            ctx.moveTo(points[n++], points[n++]);
            for (let k = 1; k < pointsPerLine; k++) {
                ctx.lineTo(points[n++], points[n++]);
            }
            ctx.stroke();
        }
    };
};

const median = (values: number[]) =>
    [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)];

const BackgroundVeil = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    // A canvas locked to WebGL can't give a 2D context, so falling back
    // remounts a fresh canvas
    const [use2d, setUse2d] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        // Skip the veil rather than break the login page without the API
        if (!canvas || typeof ResizeObserver === "undefined") {
            return;
        }
        const render = use2d
            ? createCanvas2dRenderer(canvas)
            : createWebGlRenderer(canvas);
        if (!render) {
            if (!use2d) {
                setUse2d(true);
            }
            return;
        }
        // GPU resets or too many WebGL contexts, the canvas would stay blank
        const onContextLost = () => setUse2d(true);
        canvas.addEventListener("webglcontextlost", onContextLost);
        const perlin = new Noise(2);
        const gusts = new Noise(2);
        const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;

        let animating = !reduceMotion;
        let seconds = 0;
        const draw = () => {
            const { points, pointsPerLine } = computeVeil(
                canvas.width,
                canvas.height,
                seconds,
                perlin,
                gusts,
            );
            render(points, pointsPerLine);
            // Fade in, hides any stutter while the frame rate is sampled
            canvas.style.opacity = "1";
        };

        // Observe the canvas itself, it has no size until styles are applied
        const observer = new ResizeObserver(() => {
            const dpr = getDpr();
            canvas.width = canvas.clientWidth * dpr;
            canvas.height = canvas.clientHeight * dpr;
            // Resizing clears the canvas, redraw when the loop won't
            if (!animating) {
                draw();
            }
        });
        observer.observe(canvas);

        if (!animating) {
            return () => {
                canvas.removeEventListener("webglcontextlost", onContextLost);
                observer.disconnect();
            };
        }

        let frame = 0;
        let last = 0;
        let sampled = false;
        let frames = 0;
        let gaps: number[] = [];
        let sampledMs = 0;
        const tick = (now: number) => {
            frame = requestAnimationFrame(tick);
            // Nothing to draw while the canvas is hidden
            if (canvas.width === 0 || canvas.height === 0) {
                return;
            }
            const gap = now - last;
            if (gap < FRAME_INTERVAL) {
                return;
            }
            last = now;
            seconds = now / 1000;
            draw();

            if (sampled) {
                return;
            }
            if (gap > RESET_GAP) {
                frames = 0;
                gaps = [];
                sampledMs = 0;
                return;
            }
            frames++;
            if (frames <= WARMUP_FRAMES) {
                return;
            }
            gaps.push(gap);
            sampledMs += gap;
            if (sampledMs < SAMPLE_MS || gaps.length < MIN_SAMPLES) {
                return;
            }
            sampled = true;
            if (median(gaps) > MAX_MEDIAN_GAP) {
                // Too slow to look fluid, stay on this frame
                animating = false;
                cancelAnimationFrame(frame);
            }
        };
        frame = requestAnimationFrame(tick);

        return () => {
            canvas.removeEventListener("webglcontextlost", onContextLost);
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [use2d]);

    return (
        <canvas
            key={use2d ? "2d" : "webgl"}
            ref={canvasRef}
            style={{ opacity: 0, transition: "opacity 1s ease-in" }}
        />
    );
};

export default BackgroundVeil;
